// Acknowledgment
// ---------------
// First step of the Add Application wizard. Surfaces the production /
// data-modification / service-disruption risks of running a pentest
// against the application being enrolled, and requires explicit
// acknowledgment before the user can proceed.
//
// Consent belongs at the point of decision (enrolling an app for
// testing IS the decision to test it), so we capture it here rather
// than at the first-test moment. The acknowledgment flag is persisted
// alongside the webapp record via the createWebApplication mutation
// when the wizard finishes.

import React, { useCallback, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { BUTTON_NEXT } from "@/constants/definitions";

interface AcknowledgmentProps {
  // True if the user already acknowledged this app in a prior wizard
  // pass (e.g. they hit Previous from a later step). Used to preserve
  // the checkbox state across step navigation rather than forcing
  // re-acknowledgment.
  initiallyAcknowledged?: boolean;
  // Called with BUTTON_NEXT when the user confirms.
  onComplete: (name: string) => void;
  // Fired when the user backs out of the wizard. Provided by the
  // parent NewApplication; navigates to Applications. No state is
  // persisted server-side at this step so no cleanup is needed.
  onCancel: () => void;
}

export default function Acknowledgment({
  initiallyAcknowledged = false,
  onComplete,
  onCancel,
}: AcknowledgmentProps) {
  const [acknowledged, setAcknowledged] = useState<boolean>(initiallyAcknowledged);

  const handleNext = useCallback((): void => {
    if (!acknowledged) return;
    onComplete(BUTTON_NEXT);
  }, [acknowledged, onComplete]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
        <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900 leading-relaxed space-y-3">
          <p>
            Penetration testing actively probes your application and can cause{" "}
            <strong>data modification</strong>, <strong>database entries</strong>,
            and <strong>service disruption</strong>.
          </p>
          <p>
            <strong>Testing in production is strongly discouraged.</strong> Use
            a test or staging environment where possible to avoid impacting
            real users or data.
          </p>
          <p>
            You are responsible for the environment you choose to test. Ensure
            you have appropriate authorization and backups in place before any
            test runs.
          </p>
        </div>
      </div>

      <Checkbox
        checked={acknowledged}
        onChange={(checkboxEvent) => setAcknowledged(checkboxEvent.target.checked)}
        label="I understand the risks and accept responsibility for this test environment"
      />

      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={!acknowledged}
          className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
        >
          Next
        </button>
      </div>
    </div>
  );
}
